import type {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import type {RentalOrderResponseBody, RentingStateResponseBody} from '@dms/api/rental';
import type {VehicleMileageResponseBody} from '@dms/api/sales';

import {
  ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS,
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  assistantRentalOrderContextSchema,
} from '@prompt-bridge/shared/ai-assistant-core';

import {parseDate} from 'shared/web';

const MAX_RENTING_ERRORS = 20;

type RentalOrderContextInput = {
  formatDateTime: ReturnType<typeof useDateTimeFormatter>;
  rentalOrderId: string | undefined;
  rentalOrder:
    | Pick<
        RentalOrderResponseBody,
        | 'rentalOrderId'
        | 'number'
        | 'state'
        | 'rentalVehicleId'
        | 'customerId'
        | 'plannedRentalPeriod'
      >
    | undefined;
  vehicleLabel?: string;
  customerLabel?: string;
  mileage?: Pick<VehicleMileageResponseBody, 'vehicleId' | 'mileage' | 'datedOn' | 'source'>;
  rentingState?: RentingStateResponseBody;
  cancellationReason?: string | null;
};

export const toRentalOrderAssistantContext = (input: RentalOrderContextInput) => {
  const order = input.rentalOrder;
  const period = assistantRentalOrderContextSchema.shape.plannedPeriod.safeParse(
    order?.plannedRentalPeriod
  );
  if (isNil(order) || order.rentalOrderId !== input.rentalOrderId || !period.success) {
    return undefined;
  }

  const mileage = input.mileage;
  const parsedMileage = assistantRentalOrderContextSchema.shape.lastKnownMileage.safeParse(
    !isNil(mileage) && mileage.vehicleId === order.rentalVehicleId
      ? {
          mileage: mileage.mileage,
          datedOn: mileage.datedOn,
          source: (mileage.source.name ?? mileage.source.action).slice(
            0,
            ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS
          ),
        }
      : null
  );
  const context = assistantRentalOrderContextSchema.safeParse({
    rentalOrderId: order.rentalOrderId,
    orderNumber: order.number.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    state: order.state,
    plannedPeriod: order.plannedRentalPeriod,
    plannedPeriodDisplay: {
      from: input.formatDateTime('dateTimeShort', parseDate(period.data.from)),
      to: input.formatDateTime('dateTimeShort', parseDate(period.data.to)),
    },
    vehicle: {
      id: order.rentalVehicleId,
      label: (input.vehicleLabel ?? order.rentalVehicleId)?.slice(
        0,
        ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS
      ),
    },
    customer: isNil(order.customerId)
      ? null
      : {
          id: order.customerId,
          label: (input.customerLabel ?? order.customerId).slice(
            0,
            ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS
          ),
        },
    lastKnownMileage: parsedMileage.success ? parsedMileage.data : null,
    isRentable: input.rentingState?.isRentable,
    rentingErrors: input.rentingState?.errorMessages
      .filter((message) => message.trim().length > 0)
      .slice(0, MAX_RENTING_ERRORS)
      .map((message) => message.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS)),
    cancellationReason: input.cancellationReason?.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
  });

  return context.success ? context.data : undefined;
};
